import { ReviewSourceTypes } from "@/modules/agent/types";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Tooltip,
} from "@nextui-org/react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Maximize2,
  Minimize2,
  ZoomIn,
  ZoomOut
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { LoadingSpinner } from "../spin";

// Load pdfjs worker from CDN for the latest version (secure)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export type HighlightArea = {
  pageIndex: number;
  left: number;   // measured as percentage of page width (0-100)
  top: number;    // measured as percentage of page height (0-100)
  width: number;  // measured as percentage of page width (0-100)
  height: number; // measured as percentage of page height (0-100)
};

type PDFViewerProps = {
  fileUrl: string;
  isOpen: boolean;
  onClose: () => void;
  headerTitle: string;
  setSelectedSource: React.Dispatch<
    React.SetStateAction<ReviewSourceTypes | undefined>
  >;
  selectedSource?: ReviewSourceTypes;
  listOfSourcesForSelectedSources?: ReviewSourceTypes[];
  highlight?: HighlightArea[];
  pageNumber?: number;
};

const PDFLoader = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-50">
    <LoadingSpinner />
  </div>
);

const PDFError = () => (
  <div className="w-full bg-white flex h-full px-24">
    <div className="grow w-full flex flex-col items-start justify-center gap-4">
      <h4 className="text-4xl font-semibold leading-[60px]">
        Something went wrong loading the PDF
      </h4>
    </div>
  </div>
);

export default function PDFViewer({
  fileUrl,
  isOpen,
  highlight = [],
  headerTitle,
  pageNumber = 0,
  onClose,
  selectedSource,
  setSelectedSource,
  listOfSourcesForSelectedSources,
}: PDFViewerProps) {
  // ===== STATE =====
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [inputValue, setInputValue] = useState<string>('1');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState<{ viewport: any } | null>(null);

  // ===== REFS =====
  const scrolledToSourceRef = useRef<string | number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ===== INITIALIZE ON FILE CHANGE =====
  useEffect(() => {
    setIsLoading(true);
    setNumPages(0);
    setPageInfo(null);
    setCurrentPage(pageNumber > 0 ? pageNumber : 1);
    setInputValue((pageNumber > 0 ? pageNumber : 1).toString());
    scrolledToSourceRef.current = null;
  }, [fileUrl]);

  // ===== SYNC INPUT VALUE WITH CURRENT PAGE =====
  useEffect(() => {
    if (!isInputFocused) {
      setInputValue(currentPage.toString());
    }
  }, [currentPage, isInputFocused]);

  // ===== SCROLL DETECTION TO UPDATE CURRENT PAGE =====
  useEffect(() => {
    const container = containerRef.current;
    if (!container || numPages === 0) return;

    const handleScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        const containerRect = container.getBoundingClientRect();
        const toolbar = container.querySelector('[data-toolbar="pdf-toolbar"]');
        const toolbarHeight = toolbar?.getBoundingClientRect().height || 60;
        
        let visiblePage = 1;
        let maxVisibleArea = 0;

        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          const pageElement = container.querySelector(`[data-page-number="${pageNum}"]`);
          if (pageElement) {
            const pageRect = pageElement.getBoundingClientRect();
            const visibleTop = Math.max(pageRect.top, containerRect.top + toolbarHeight);
            const visibleBottom = Math.min(pageRect.bottom, containerRect.bottom);
            const visibleHeight = Math.max(0, visibleBottom - visibleTop);
            const visibleArea = visibleHeight * pageRect.width;

            if (visibleArea > maxVisibleArea) {
              maxVisibleArea = visibleArea;
              visiblePage = pageNum;
            }
          }
        }

        setCurrentPage(visiblePage);
      }, 100);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [numPages]);

  // ===== AUTO-SCROLL TO HIGHLIGHT ON SOURCE CHANGE =====
  useEffect(() => {
    if (!selectedSource || !containerRef.current) return;

    const sourceKey = selectedSource.source_number || '';
    
    // Only auto-scroll once per source selection
    if (scrolledToSourceRef.current === sourceKey) return;

    // Helper function to perform the scroll
    const scrollToHighlight = () => {
      const container = containerRef.current;
      if (!container) return false;

      const firstHighlight = container.querySelector('.highlight-area');
      const toolbar = container.querySelector('[data-toolbar="pdf-toolbar"]');
      const toolbarHeight = toolbar?.getBoundingClientRect().height || 60;

      if (firstHighlight) {
        const containerRect = container.getBoundingClientRect();
        const highlightRect = firstHighlight.getBoundingClientRect();
        const scrollTop = highlightRect.top - containerRect.top + container.scrollTop - toolbarHeight - 20;
        
        container.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth'
        });
        return true;
      } else if (pageNumber > 0) {
        const pageElement = container.querySelector(`[data-page-number="${pageNumber}"]`);
        if (pageElement) {
          const containerRect = container.getBoundingClientRect();
          const pageRect = pageElement.getBoundingClientRect();
          const scrollTop = pageRect.top - containerRect.top + container.scrollTop - toolbarHeight - 20;
          
          container.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: 'smooth'
          });
          return true;
        }
      }
      return false;
    };

    // If PDF is not loaded yet, wait for it
    if (numPages === 0 || isLoading) {
      return;
    }

    // Try to scroll with retries for highlights to render
    let attempts = 0;
    const maxAttempts = 10;
    const attemptInterval = 200;

    const tryScroll = () => {
      attempts++;
      const success = scrollToHighlight();
      
      if (success) {
        scrolledToSourceRef.current = sourceKey;
      } else if (attempts < maxAttempts) {
        setTimeout(tryScroll, attemptInterval);
      } else {
        // Even if no highlight found, mark as scrolled to prevent infinite retries
        scrolledToSourceRef.current = sourceKey;
      }
    };

    // Start attempting after a short delay
    const timeoutId = setTimeout(tryScroll, 300);
    
    return () => clearTimeout(timeoutId);
  }, [selectedSource, numPages, isLoading, pageNumber]);

  // ===== COMPUTED VALUES =====
  const currentSourceIndex = useMemo(() => {
    if (!listOfSourcesForSelectedSources || !selectedSource) return -1;
    return listOfSourcesForSelectedSources.findIndex(
      (d) => d.source_number === selectedSource.source_number
    );
  }, [listOfSourcesForSelectedSources, selectedSource]);

  const sourceDisplayIndex = currentSourceIndex >= 0 ? currentSourceIndex + 1 : 0;
  const canNavigatePrevious = currentSourceIndex > 0;
  const canNavigateNext = currentSourceIndex >= 0 && 
    listOfSourcesForSelectedSources && 
    currentSourceIndex < listOfSourcesForSelectedSources.length - 1;

  // ===== HANDLERS =====
  const handlePrevious = useCallback(() => {
    if (canNavigatePrevious && listOfSourcesForSelectedSources) {
      setSelectedSource(listOfSourcesForSelectedSources[currentSourceIndex - 1]);
    }
  }, [canNavigatePrevious, currentSourceIndex, listOfSourcesForSelectedSources, setSelectedSource]);

  const handleNext = useCallback(() => {
    if (canNavigateNext && listOfSourcesForSelectedSources) {
      setSelectedSource(listOfSourcesForSelectedSources[currentSourceIndex + 1]);
    }
  }, [canNavigateNext, currentSourceIndex, listOfSourcesForSelectedSources, setSelectedSource]);

  const handleZoomIn = useCallback(() => {
    setScale(s => Math.min(s + 0.2, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale(s => Math.max(s - 0.2, 0.5));
  }, []);

  const handleFullScreen = useCallback(() => {
    setIsFullscreen(f => !f);
  }, []);

  const goToPage = useCallback((n: number) => {
    if (n < 1 || n > numPages || !containerRef.current) return;
    
    setCurrentPage(n);
    
    requestAnimationFrame(() => {
      const pageElement = containerRef.current?.querySelector(`[data-page-number="${n}"]`);
      if (pageElement) {
        const container = containerRef.current;
        const toolbar = containerRef.current?.querySelector('[data-toolbar="pdf-toolbar"]');
        const toolbarHeight = toolbar?.getBoundingClientRect().height || 60;
        const containerRect = container!.getBoundingClientRect();
        const pageRect = pageElement.getBoundingClientRect();
        const scrollTop = pageRect.top - containerRect.top + container!.scrollTop - toolbarHeight - 20;
        
        container!.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth'
        });
      }
    });
  }, [numPages]);

  const handlePageRenderSuccess = useCallback((page: any) => {
    const viewport = page.getViewport({ scale });
    setPageInfo({ viewport });
  }, [scale]);

  const getHighlightStyle = useCallback((area: HighlightArea) => {
    if (!pageInfo) return { display: "none" as const };
    
    return {
      position: "absolute" as const,
      left: `${area.left}%`,
      top: `${area.top}%`,
      width: `${area.width}%`,
      height: `${area.height}%`,
      background: "rgba(255, 255, 0, 0.4)",
      border: "1px solid rgba(255, 255, 0, 0.8)",
      pointerEvents: "none" as const,
      zIndex: 5,
      boxSizing: "border-box" as const,
    };
  }, [pageInfo]);

  // Modal full screen styling with consistent sizing
  const modalClass = isFullscreen
    ? "fixed inset-0 z-[9999] bg-black bg-opacity-90 rounded-none"
    : "max-h-[calc(100vh-32px)] max-w-[calc(100vw-200px)] !rounded-[8px] overflow-y-hidden !my-auto";

  const modalStyle = isFullscreen 
    ? { borderRadius: "0 !important" }
    : { 
        borderRadius: "8px !important",
        minWidth: "800px",
        minHeight: "600px",
        width: "90vw",
        height: "90vh"
      };

  return (
    <Modal
      className={modalClass}
      style={modalStyle}
      size="full"
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalContent className="flex flex-col h-full">
        {() => (
          <>
            <ModalHeader className="border-b border-solid border-[#E4E4E7] flex-shrink-0">
              <div className="flex gap-3 items-center ">
                <div className="flex gap-1 items-center pr-3 border-r border-[#E4E4E7] border-solid">
                  <Tooltip
                    content="Previous Reference"
                    classNames={{
                      content:
                        "bg-black bg-opacity-80 backdrop-blur-md text-white text-tiny",
                      arrow: "bg-black bg-opacity-80 backdrop-blur-md",
                    }}
                  >
                    <div
                      className="select-none bg-white cursor-pointer border-[1px] h-5 w-5 flex items-center justify-center rounded-[50%] border-solid border-[#E4E4E7]"
                      onClick={handlePrevious}
                    >
                      <ChevronLeft
                        color={canNavigatePrevious ? "#A1A1AA" : "#E4E4E7"}
                        size={16}
                      />
                    </div>
                  </Tooltip>
                  <div className="text-[12px] leading-5 font-medium text-[#71717A] w-11 text-center">
                    {sourceDisplayIndex} of {listOfSourcesForSelectedSources?.length}
                  </div>
                  <Tooltip
                    content="Next Reference"
                    classNames={{
                      content:
                        "bg-black bg-opacity-80 backdrop-blur-md text-white text-tiny",
                      arrow: "bg-black bg-opacity-80 backdrop-blur-md",
                    }}
                  >
                    <div
                      className="select-none bg-white cursor-pointer border-[1px] h-5 w-5 flex items-center justify-center rounded-[50%] border-solid border-[#E4E4E7]"
                      onClick={handleNext}
                    >
                      <ChevronRight
                        color={canNavigateNext ? "#A1A1AA" : "#E4E4E7"}
                        size={16}
                      />
                    </div>
                  </Tooltip>
                </div>
                <p className="text-sm font-medium">{headerTitle}</p>
              </div>
            </ModalHeader>
            <ModalBody className="flex-1 overflow-auto px-0 py-0 min-h-0">
              <div
                ref={containerRef}
                data-container="pdf-container"
                style={{
                  overflowY: "auto",
                  background: "#f6f6f7",
                  position: "relative",
                  height: "100%",
                  width: "100%",
                }}
              >
                {/* Custom Toolbar */}
                <div
                  data-toolbar="pdf-toolbar"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    background: "#fff",
                    borderBottom: "1px solid #e4e4e7",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
                  }}
                >
                  {/* Page navigation */}
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flex: 1 }}>
                    <Tooltip content="First page">
                      <button
                        onClick={() => goToPage(1)}
                        disabled={currentPage === 1}
                        className="p-1 rounded-lg bg-white  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronsLeft size={16} className="text-gray-600" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Previous page">
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage <= 1}
                        className="p-1 rounded-lg bg-white  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft size={16} className="text-gray-600" />
                      </button>
                    </Tooltip>
                    
                    <div className="flex items-center gap-2 mx-2">
                      <span className="text-sm font-medium text-gray-700">Page</span>
                      <input
                        type="number"
                        value={inputValue}
                        min={1}
                        max={numPages}
                        className="w-16 p-1 text-sm text-center border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        onFocus={() => {
                          setIsInputFocused(true);
                        }}
                        onChange={(e) => {
                          const value = e.target.value;
                          
                          // Allow empty string for clearing
                          if (value === '') {
                            setInputValue(value);
                            return;
                          }
                          
                          const pageNum = Number(value);
                          
                          // Prevent typing invalid numbers, but allow partial input
                          if (isNaN(pageNum) || pageNum < 0) {
                            return; // Don't update if invalid
                          }
                          
                          // Allow typing up to the max pages, but don't navigate beyond valid range
                          if (pageNum > numPages) {
                            // Don't allow typing beyond max pages
                            return;
                          }
                          
                          setInputValue(value);
                          
                          // Only navigate if the page number is valid and within range
                          if (pageNum >= 1 && pageNum <= numPages) {
                            goToPage(pageNum);
                          }
                        }}
                        onBlur={(e) => {
                          setIsInputFocused(false);
                          const value = e.target.value;
                          if (value === '' || Number(value) < 1 || Number(value) > numPages || isNaN(Number(value))) {
                            // Reset to current page if invalid or empty on blur
                            setInputValue(currentPage.toString());
                          } else {
                            const pageNum = Number(value);
                            if (pageNum !== currentPage) {
                              goToPage(pageNum);
                            }
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const value = e.currentTarget.value;
                            if (value !== '' && Number(value) >= 1 && Number(value) <= numPages) {
                              goToPage(Number(value));
                            } else {
                              setInputValue(currentPage.toString());
                            }
                            e.currentTarget.blur();
                          }
                        }}
                      />
                      <span className="text-sm text-gray-500">of {numPages || "?"}</span>
                    </div>

                    <Tooltip content="Next page">
                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage >= numPages}
                        className="p-1 rounded-lg bg-white  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight size={16} className="text-gray-600" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Last page">
                      <button
                        onClick={() => goToPage(numPages)}
                        disabled={currentPage === numPages}
                        className="p-1 rounded-lg bg-white  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronsRight size={16} className="text-gray-600" />
                      </button>
                    </Tooltip>
                  </div>

                  {/* Zoom Controls - Centered */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", flex: 1 }}>
                    <Tooltip content="Zoom out">
                      <button
                        onClick={handleZoomOut}
                        disabled={scale <= 0.5}
                        className="p-1 rounded-lg bg-white  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ZoomOut size={16} className="text-gray-600" />
                      </button>
                    </Tooltip>
                    <div className="px-3 py-0 bg-gray-50 rounded-md ">
                      <span className="text-sm font-medium text-gray-700">
                        {Math.round(scale * 100)}%
                      </span>
                    </div>
                    <Tooltip content="Zoom in">
                      <button
                        onClick={handleZoomIn}
                        disabled={scale >= 3}
                        className="p-1 rounded-lg bg-white  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ZoomIn size={16} className="text-gray-600" />
                      </button>
                    </Tooltip>
                  </div>

                  {/* Fullscreen */}
                  <div style={{ display: "flex", justifyContent: "flex-end", flex: 1 }}>
                    <Tooltip content={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
                      <button
                        onClick={handleFullScreen}
                        className="p-1 rounded-lg bg-white  hover:bg-gray-50 transition-colors"
                      >
                        {isFullscreen ? (
                          <Minimize2 size={16} className="text-gray-600" />
                        ) : (
                          <Maximize2 size={16} className="text-gray-600" />
                        )}
                      </button>
                    </Tooltip>
                  </div>
                </div>
                <Document
                  file={fileUrl}
                  loading={<PDFLoader />}
                  error={PDFError}
                  onLoadSuccess={({ numPages }) => {
                    setNumPages(numPages);
                    setIsLoading(false);
                  }}
                  onLoadError={() => {
                    setIsLoading(false);
                    setNumPages(0);
                  }}
                  className="text-center"
                >
                  <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: "20px", padding: "20px 0 0 0", minHeight: "100%" }}>
                    {/* Show loading state while switching files */}
                    {isLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-50">
                        <LoadingSpinner />
                      </div>
                    )}
                    {/* Render all pages only when not loading and numPages > 0 */}
                    {!isLoading && numPages > 0 && Array.from(new Array(numPages), (el, index) => (
                      <div 
                        key={`page_${index + 1}`} 
                        data-page-number={index + 1}
                        style={{ position: "relative", display: "inline-block" }}
                      >
                        <Page
                          pageNumber={index + 1}
                          scale={scale}
                          width={isFullscreen ? undefined : 800}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                          loading=""
                          onRenderSuccess={(page) => {
                            // Only update pageInfo for the current page or first page
                            if (index + 1 === currentPage || (currentPage === 1 && index === 0)) {
                              handlePageRenderSuccess(page);
                            }
                          }}
                        />
                        {/* highlights for this specific page */}
                        {pageInfo &&
                          highlight
                            .filter((h) => h.pageIndex === index)
                            .sort((a, b) => a.top - b.top)
                            .map((area, idx) => (
                              <div
                                key={`highlight-${index}-${idx}`}
                                className="highlight-area"
                                style={getHighlightStyle(area)}
                              />
                            ))}
                      </div>
                    ))}
                  </div>
                </Document>
              </div>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}